import React, { useContext, useEffect, useState } from 'react';
import camelCase from 'lodash/camelCase';
import isFunction from 'lodash/isFunction';
import { useRouter } from 'next/router';

import cypressIds from 'Components/cypress_ids.json';
import { AppContext } from 'Context/AppContext';
import { useListType } from 'Stores/ListStore/ListStore';
import {
	useIsShortlisted,
	useShortlistStore,
	useToggleUnshortlistCacheById,
} from 'Stores/ShortlistStore/ShortlistStore';
import { LIST_TYPES } from 'Utilities/consts';
import { applyCypressData } from 'Utilities/helpers';
import ROUTES from 'Utilities/routes';

import { ShortlistStar } from './ShortlistStar';

interface OnClickShortlistArgs {
	id: number;
	isChecked: boolean;
	page: string;
}

interface ShortlistButtonProps {
	className?: string;
	id: number;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	onClickShortlist?: (args: OnClickShortlistArgs) => void;
	page: string;
	rounded?: boolean;
}

export const ShortlistButton: React.FC<ShortlistButtonProps> = (props) => {
	const { id, onClickShortlist, page, ...favouriteStarProps } = props;

	const appContext = useContext(AppContext);

	const isShortlisted = useIsShortlisted(id);
	const toggleShortlisting = useShortlistStore((store) => store.toggleShortlisting);
	const toggleUnshortlistCache = useToggleUnshortlistCacheById();
	const listType = useListType();
	const isShortlistListType = listType === LIST_TYPES.shortlist;
	const router = useRouter();
	const { route } = router;
	const isVehicleListPage = route === ROUTES.VEHICLE_LIST.href;
	const shouldToggleUnshortlistCache = isShortlistListType && isVehicleListPage;

	const [isChecked, setIsChecked] = useState(false);
	const [isBusy, setIsBusy] = useState(false);

	useEffect(() => {
		setIsChecked(isShortlisted);
	}, [isShortlisted]);

	const user = appContext?.state?.user;

	if (!user) {
		return null;
	}

	const toggleIsChecked = () => {
		setIsChecked((state) => !state);
	};

	const handleShortlistChange = async (nextChecked: boolean) => {
		const error = await toggleShortlisting({ checked: nextChecked, id });

		if (!error) {
			return;
		}

		toggleIsChecked(); // error happened, toggle state back to previous one.
	};

	const onChange = async (e: React.SyntheticEvent) => {
		e.stopPropagation();
		if (isBusy) {
			return;
		}

		setIsBusy(true);
		toggleIsChecked(); // toggle state to make optimistic UI

		const nextChecked = !isChecked;

		if (isFunction(onClickShortlist)) {
			onClickShortlist({ id, isChecked: nextChecked, page });
		}

		const shouldHandleShortlistChange = nextChecked || !isShortlistListType || !isVehicleListPage;
		if (shouldHandleShortlistChange) {
			await handleShortlistChange(nextChecked);
		}

		if (shouldToggleUnshortlistCache) {
			toggleUnshortlistCache({ checked: isChecked, id });
		}

		setIsBusy(false);
	};

	const inputProps = {
		...applyCypressData(camelCase(`${page} ${cypressIds.buttons?.vehicleCard?.shortList}`)),
		'data-testid': `shortlist-button-${id}`,
	} as React.InputHTMLAttributes<HTMLInputElement>;

	return <ShortlistStar {...favouriteStarProps} checked={isChecked} inputProps={inputProps} onChange={onChange} />;
};
